import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "ticketino-api-client";
import { useLanguage } from "../hooks/useLanguage";
import { useOrder } from "../hooks/useOrder";
import ticketTypesByBookingType from "../utils/ticket-types";
import ticketTypesRallyCrossSelling from "../utils/ticket-types-rally-cross-seling";

import Header from "./components/Header";
import Footer from "./components/Footer";
import Loading from "./components/Loading";

import logoCityRally from "../images/logo_city_rally.png";
import iconShow from "../images/icons/show.svg";
import iconHide from "../images/icons/hide.svg";
import iconArrowUpRed from "../images/icons/arrow-up-red.svg";

const Tickets = () => {
    const [resources, setResources] = useState({});
    const [quantitiesTicketTypes, setQuantitiesTicketTypes] = useState({});
    const [quantitiesRally, setQuantitiesRally] = useState({});
    const [errorMsg, setErrorMsg] = useState(null);
    const [showDiscounted, setShowDiscounted] = useState(false);
    const [showRallyTickets, setShowRallyTickets] = useState(false);

    const { bookingType } = useParams();
    const { language } = useLanguage();

    const navigate = useNavigate();
    let baseUrl = process.env.REACT_APP_BASEURL_API;
    api.defaults.baseURL = baseUrl;

    const order = useOrder(baseUrl);

    useEffect(() => {
        if (language) {
            requestResources();
        }
    }, [language]);

    const requestResources = async () => {
        await axios
            .get(`form/resources/${language}`)
            .then((res) => {
                setResources(res.data?.translation);
            })
            .catch((error) => console.error(error.response.data));
    };

    // Get ticket types by bookingType
    const ticketGroup = ticketTypesByBookingType[bookingType];
    const ticketTypes = ticketGroup?.items || [];
    const groupTitle = ticketGroup?.title ? (ticketGroup.title[language] || ticketGroup.title['de']) : '';

    // Helper to get name in selected language, fallback to 'de'
    const getTicketTypeName = (ticketType) => {
        if (!ticketType.info?.name) return "";
        return ticketType.info.name[language] || ticketType.info.name["de"] || "";
    };

    // Helper to format rally dropdown text with person ranges
    const getRallyDropdownText = (qty) => {
        if (qty === 0) return '0';
        if (qty === 1) return `1 (3 - 5 ${resources?.TicketsRallyPeople})`;
        const start = (qty - 1) * 5 + 1;
        const end = qty * 5;
        return `${qty} (${start} - ${end} ${resources?.TicketsRallyPeople})`;
    };

    // Handle quantity change for normal ticket types
    const handleQuantityChangeTicketTypes = (ticketId, value) => {
        setQuantitiesTicketTypes(prev => ({ ...prev, [ticketId]: value }));
    };

    // Handle quantity change for City Rally tickets
    const handleQuantityChangeRally = (ticketId, value) => {
        setQuantitiesRally(prev => ({ ...prev, [ticketId]: value }));
    };

    const handleNext = () => {
        setErrorMsg(null);

        // Prepare selected tickets data
        const selectedTickets = ticketTypes
            .filter(ticketType => quantitiesTicketTypes[ticketType.id] > 0)
            .map(ticketType => ({
                externalTicketCode: ticketType.externalTicketCode,
                quantity: quantitiesTicketTypes[ticketType.id]
            }));

        // For school groups, automatically add 1 companion ticket
        if (bookingType === "schoolgroup") {
            selectedTickets.push({
                externalTicketCode: "school companion",
                quantity: 1
            });
        }

        // Validate supervisor requirements for birthday and school groups
        if (ticketGroup?.supervisorRules && (bookingType === "birthday" || bookingType === "schoolgroup")) {
            const rules = ticketGroup.supervisorRules;

            // Count children tickets
            let childrenCount = 0;
            ticketTypes.forEach(ticketType => {
                const qty = quantitiesTicketTypes[ticketType.id] || 0;
                if (rules.childTicketCodes.includes(ticketType.externalTicketCode)) {
                    childrenCount += qty;
                }
            });

            // Count supervisor tickets
            let supervisorCount = 0;
            ticketTypes.forEach(ticketType => {
                const qty = quantitiesTicketTypes[ticketType.id] || 0;
                if (rules.supervisorTicketCodes.includes(ticketType.externalTicketCode)) {
                    supervisorCount += qty;
                }
            });

            // Calculate total required supervisors based on all children
            const totalRequiredSupervisors = Math.ceil(childrenCount / rules.childrenPerSupervisor);

            // For school groups, account for the free companion ticket
            let requiredAdditionalSupervisors = totalRequiredSupervisors;
            if (bookingType === "schoolgroup" && rules.freeCompanionTickets) {
                supervisorCount += rules.freeCompanionTickets;
                // Calculate how many additional supervisors need to be selected (beyond the free companion)
                requiredAdditionalSupervisors = Math.max(0, totalRequiredSupervisors - rules.freeCompanionTickets);
            }

            // Check if enough supervisors are selected
            if (childrenCount > 0 && supervisorCount < totalRequiredSupervisors) {
                const errorKey = bookingType === "birthday" ? "TicketsSupervisorErrorBirthday" : "TicketsSupervisorErrorSchool";
                setErrorMsg({
                    type: "supervisor",
                    message: resources?.[errorKey]?.replace('{supervisors}', requiredAdditionalSupervisors)
                });
                return;
            }
        }

        // check if user selected acceptable quantity of tickets
        const totalQuantity = selectedTickets.reduce((sum, t) => sum + t.quantity, 0);
        const minQuantity = ticketGroup?.minQuantity || 0;
        const maxQuantity = ticketGroup?.maxQuantity || Infinity;

        // Calculate already booked tickets (tickets with timeOfEntryFrom)
        const alreadyBookedQuantity = (order?.tickets || []).filter(ticket => {
            return ticket.timeOfEntryFrom;
        }).length;

        // Calculate total tickets including already booked ones
        const totalIncludingAlreadyBooked = alreadyBookedQuantity + totalQuantity;

        if (totalQuantity < minQuantity) {
            setErrorMsg({ type: "min" });
            return;
        }
        if (totalIncludingAlreadyBooked > maxQuantity) {
            setErrorMsg({ type: "max" });
            return;
        }

        // Prepare session data (overwrite all previous tickets)
        const sessionData = {
            eventGroupId: ticketGroup.eventGroupId,
            tickets: selectedTickets
        };

        // Save to localStorage (replace all tickets)
        localStorage.setItem('selectedTickets', JSON.stringify(sessionData));
        localStorage.setItem("bookingType", bookingType);

        // Rally Cross-Selling: collect selected tickets
        const selectedCrossSellingTickets = ticketTypesRallyCrossSelling.items
            .filter(ticketType => quantitiesRally[ticketType.id] > 0)
            .map(ticketType => ({
                externalTicketCode: ticketType.externalTicketCode,
                quantity: quantitiesRally[ticketType.id]
            }));

        // Calculate total quantity of rally tickets
        const totalRallyQuantity = selectedCrossSellingTickets.reduce((sum, t) => sum + t.quantity, 0);

        // Add City Rally Introduction ticket if total quantity of Rally tickets is 4 or more
        if (totalRallyQuantity >= 4) {
            selectedCrossSellingTickets.push({
                externalTicketCode: "city rally introduction",
                quantity: 1
            });
        }

        // Save cross-selling tickets to localStorage
        const sessionDataCrossSelling = {
            eventGroupId: 3553,
            tickets: selectedCrossSellingTickets
        };

        localStorage.setItem('selectedTicketsCrossSelling', JSON.stringify(sessionDataCrossSelling));

        // Navigate to next page
        if (bookingType === 'birthday') {
            navigate(`/${language}/addons-birthday`);
            return;
        } else {
            navigate(`/${language}/addons`);
            return;
        }
    };

    const canProceed = ticketTypes.some(ticketType => quantitiesTicketTypes[ticketType.id] > 0);

    // Calculate total selected normal tickets
    const totalNormalTickets = Object.values(quantitiesTicketTypes).reduce((sum, qty) => sum + (qty || 0), 0);
    // Show City Rally to everyone, all the time
    //const showCityRally = true;
    const showCityRally = bookingType !== "birthday";
    //const showCityRally = bookingType === "groupvisit" && totalNormalTickets >= 37;
    //const showCityRally = bookingType === "groupvisit" && totalNormalTickets >= 1;

    // Split ticket types into normal and discounted
    const normalTicketTypes = ticketTypes.filter(t => !t.isDiscountTicketType);
    const discountedTicketTypes = ticketTypes.filter(t => t.isDiscountTicketType);

    return (
        <>
            <Header language={language} pagePath={`tickets/${bookingType}`} pageTitle={`${groupTitle}: ${resources?.TicketsChoose}`}
                ticketCount={order?.tickets?.length || 0} />

            <div className="page-container">
                <div className="container wrapper">

                    {ticketTypes && ticketTypes.length &&
                        <>
                            {/* Ticket types table in a styled box */}
                            <div>
                                <table className="table table-no-border table-align-middle" style={{ marginBottom: 0, width: "100%" }}>
                                    <tbody>
                                        {normalTicketTypes.map((ticketType, idx) => (
                                            <tr key={ticketType.id || idx}>
                                                <td>
                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                                        <span>{getTicketTypeName(ticketType)}</span>
                                                    </label>
                                                </td>
                                                <td colSpan={2} style={{ width: "1%", paddingRight: "0" }}>
                                                    <div style={{
                                                        display: "flex",
                                                        justifyContent: "flex-end",
                                                        alignItems: "center",
                                                        gap: "2rem"
                                                    }}>
                                                        <span style={{ minWidth: "120px", textAlign: "right" }}>
                                                            {ticketType.currency} {ticketType.price.toFixed(2)}
                                                        </span>
                                                        <select
                                                            className="form-select qtd-select"
                                                            value={quantitiesTicketTypes[ticketType.id] || 0}
                                                            onChange={e => handleQuantityChangeTicketTypes(ticketType.id, Number(e.target.value))}
                                                        >
                                                            {[...Array((ticketType.quantity || 10) + 1).keys()].map(qty => (
                                                                <option key={qty} value={qty}>{qty}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}

                                        {/* Show/hide discounted tickets toggle */}
                                        {discountedTicketTypes.length > 0 && (
                                            <>
                                                <tr>
                                                    <td colSpan={3} style={{ paddingTop: "0px" }}>
                                                        <hr className="ticket-separator" />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td colSpan={3} style={{ paddingTop: "0px" }}>
                                                        <span style={{ cursor: 'pointer' }}
                                                            onClick={() => setShowDiscounted(v => !v)}>
                                                            <b>{resources?.TicketsChooseDiscountedTickets}</b>
                                                            <img
                                                                src={showDiscounted ? iconHide : iconShow}
                                                                alt=""
                                                                style={{ marginLeft: 8, marginTop: -2, verticalAlign: 'middle', height: 20 }}
                                                            />
                                                        </span>
                                                    </td>
                                                </tr>
                                            </>
                                        )}

                                        {/* Discounted ticket types, only visible if showDiscounted is true */}
                                        {showDiscounted && discountedTicketTypes.map((ticketType, idx) => (
                                            <tr key={ticketType.id || idx}>
                                                <td>
                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                                        <span>{getTicketTypeName(ticketType)}</span>
                                                    </label>
                                                </td>
                                                <td colSpan={2} style={{ width: "1%", paddingRight: "0" }}>
                                                    <div style={{
                                                        display: "flex",
                                                        justifyContent: "flex-end",
                                                        alignItems: "center",
                                                        gap: "2rem"
                                                    }}>
                                                        <span style={{ minWidth: "120px", textAlign: "right" }}>
                                                            {ticketType.currency} {ticketType.price.toFixed(2)}
                                                        </span>
                                                        <select
                                                            className="form-select qtd-select"
                                                            value={quantitiesTicketTypes[ticketType.id] || 0}
                                                            onChange={e => handleQuantityChangeTicketTypes(ticketType.id, Number(e.target.value))}
                                                        >
                                                            {[...Array((ticketType.quantity || 10) + 1).keys()].map(qty => (
                                                                <option key={qty} value={qty}>{qty}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Companion Person Information - Only for School Groups */}
                            {bookingType === "schoolgroup" && (
                                <>
                                    <div className="row">
                                        <div className="col-md-12">
                                            <hr className="ticket-separator" />
                                        </div>
                                    </div>

                                    <div className="row mt-1 text-center custom-box-red">
                                        <p className="title">{resources?.TicketsSchoolCompanionTitle}</p>
                                        <p>{resources?.TicketsSchoolCompanionDescription}</p>
                                    </div>
                                </>
                            )}

                            {/* Rally Cross-Selling Ticket Types */}
                            {/* Only show City Rally if groupvisit and 37+ normal tickets */}
                            {showCityRally && (
                                <>
                                    <div className="row">
                                        <div className="col-md-12">
                                            <hr className="ticket-separator" />
                                        </div>
                                    </div>

                                    {!showRallyTickets && (
                                        <div className="row mt-1 custom-box-red">
                                            <div className="col-12 text-center">
                                                <p>{resources?.TicketsCityRallyTitle}</p>
                                                <div className="mt-4">
                                                    <button
                                                        className="btn-custom white"
                                                        onClick={() => setShowRallyTickets(true)}
                                                    >
                                                        {resources?.TicketsCityRallyBookNow}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {showRallyTickets && (
                                        <>
                                            <div className="row mt-5">
                                                <div className="col-12 text-center">
                                                    <img src={logoCityRally} alt="City Rally Logo" style={{ maxWidth: "220px", height: "auto" }} />
                                                </div>
                                            </div>

                                            <div className="row mt-5 text-center">
                                                <p className="title">Tickets</p>
                                                <p>{resources?.TicketsCityRallyDesc}</p>
                                                <p><strong>{resources?.TicketsCityRallyProfit}</strong></p>
                                            </div>

                                            <div className="row text-center" style={{ marginTop: '1rem' }}>
                                                <div className="col-12">
                                                    <img
                                                        src={iconArrowUpRed}
                                                        alt="Close Rally Section"
                                                        style={{
                                                            cursor: 'pointer',
                                                            height: 25,
                                                        }}
                                                        onClick={() => setShowRallyTickets(false)}
                                                    />
                                                </div>
                                            </div>


                                            <table className="mt-4 table table-no-border table-align-middle" style={{ marginBottom: 0, width: "100%" }}>
                                                <tbody>
                                                    {ticketTypesRallyCrossSelling.items.map((ticketType, idx) => (
                                                        <tr key={ticketType.id || idx}>
                                                            <td>
                                                                <label style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                                                    <span>{ticketType.info.name[language] || ticketType.info.name["de"]}</span>
                                                                </label>
                                                            </td>
                                                            <td colSpan={2} style={{ width: "1%", paddingRight: "0" }}>
                                                                <div style={{
                                                                    display: "flex",
                                                                    justifyContent: "flex-end",
                                                                    alignItems: "center",
                                                                    gap: "2rem"
                                                                }}>
                                                                    <span style={{
                                                                        minWidth: "120px",
                                                                        textAlign: "right",
                                                                        textTransform: "uppercase",
                                                                        textDecoration: "line-through",
                                                                        color: "#888"
                                                                    }}>
                                                                        {ticketType.currency} {ticketType.oldPrice.toFixed(2)}
                                                                    </span>
                                                                    <span style={{ minWidth: "120px", textAlign: "right" }}>
                                                                        {ticketType.currency} {ticketType.price.toFixed(2)}
                                                                    </span>
                                                                    <select
                                                                        className="form-select qtd-select"
                                                                        value={quantitiesRally[ticketType.id] || 0}
                                                                        onChange={e => handleQuantityChangeRally(ticketType.id, Number(e.target.value))}
                                                                    >
                                                                        {[...Array((ticketType.quantity || 10) + 1).keys()].map(qty => (
                                                                            <option key={qty} value={qty}>{getRallyDropdownText(qty)}</option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>

                                            {/* Introduction City Rally Ticket Type */}
                                            {/* ticket type added to order when 4 or more Rally ticket Types are selected */}
                                            <div className="row mt-5 text-center">
                                                <p className="title">{resources?.TicketsIntroductionCityRally}</p>
                                                <p>{resources?.TicketsIntroductionCityRallyDesc}</p>
                                            </div>

                                            <table className="mt-4 table table-no-border table-align-middle" style={{ marginBottom: 0, width: "100%" }}>
                                                <tbody>
                                                    <tr>
                                                        <td>
                                                            <label style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                                                <span>Einführung City Rally *</span>
                                                            </label>
                                                        </td>
                                                        <td colSpan={2} style={{ width: "1%", paddingRight: "0" }}>
                                                            <div style={{
                                                                display: "flex",
                                                                justifyContent: "flex-end",
                                                                alignItems: "center",
                                                                gap: "2rem"
                                                            }}>
                                                                <span style={{ minWidth: "120px", textAlign: "right" }}>
                                                                    30 CHF
                                                                </span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </>
                                    )}
                                </>
                            )}

                            <div className="row mt-5 text-center">
                                <p dangerouslySetInnerHTML={{ __html: resources?.TicketsIntroductionCityRallyHelpLine }}></p>
                            </div>

                            {/* Error message */}
                            {errorMsg && (
                                errorMsg.type === "max"
                                    ? <div className="custom-box text-red mt-3" dangerouslySetInnerHTML={{
                                        __html: resources?.TicketsMaxQuantity?.replace('{quantity}', ticketGroup?.maxQuantity)
                                    }} />
                                    : errorMsg.type === "min"
                                        ? <div className="custom-box text-red mt-3" dangerouslySetInnerHTML={{
                                            __html: resources?.TicketsMinQuantity?.replace('{quantity}', ticketGroup?.minQuantity)
                                        }} />
                                        : errorMsg.type === "supervisor"
                                            ? <div className="custom-box text-red mt-3">{errorMsg.message}</div>
                                            : null
                            )}

                            <div className="row mt-5">
                                <div className="col-6">
                                    <button
                                        className="btn-custom white"
                                        type="submit"
                                        onClick={() => navigate(-1)}
                                    >
                                        {resources?._Back}
                                    </button>
                                </div>

                                <div className="col-6 text-end">
                                    <button
                                        className="btn-custom"
                                        type="submit"
                                        onClick={handleNext}
                                        disabled={!canProceed}
                                    >
                                        {resources?._Next}
                                    </button>
                                </div>
                            </div>
                        </>
                    }
                </div>
            </div>

            <Footer language={language} />
        </>
    )
}

export default Tickets